import { API_ROUTES, BASE_URL } from '../../config/api-routes';
import {
  parseRankedAiPromptList,
  type RankedPromptChoice,
} from '../../utils/parseRankedAiPromptList';
import { tierFromIndexForBadge } from '../../utils/promptRecommendationBadge';
import { loadCachedRolePrompts, saveCachedRolePrompts } from '../../utils/rolePromptCache';
import type { AppPrompt, CandidateProfileMeta } from './promptsV2Types';

export const ROLE_PROMPT_POOL_SIZE = 30;
export const DEFAULT_JOB_INDUSTRY = 'General';
export const DEFAULT_EXPERIENCE_LEVEL = 'Mid-level';

const ROLE_PROMPT_AI_INSTRUCTIONS = `Return exactly ${ROLE_PROMPT_POOL_SIZE} screening interview questions for this role.
Order them from most to least effective for screening.
Ground questions in the candidate resume experience and skills when provided.
Use this format:
1. [MOST RECOMMENDED] <single best question>
2. [RECOMMENDED] <second best question>
3. [RECOMMENDED] <third best question>
4. [RECOMMENDED] <fourth best question>
5-${ROLE_PROMPT_POOL_SIZE}. <question only, no badge tags>
One question per line, numbered 1-${ROLE_PROMPT_POOL_SIZE}, no group headings.`;

export function choicesFromPromptTexts(texts: string[]): RankedPromptChoice[] {
  return texts
    .map((text) => String(text || '').trim())
    .filter(Boolean)
    .map((text, index) => ({
      text,
      tier: tierFromIndexForBadge(index),
    }));
}

export function withRolePromptMetaDefaults(
  profile: CandidateProfileMeta | null | undefined
): Required<Pick<CandidateProfileMeta, 'jobTitle' | 'jobIndustry' | 'level'>> {
  return {
    jobTitle: String(profile?.jobTitle || '').trim(),
    jobIndustry: String(profile?.jobIndustry || '').trim() || DEFAULT_JOB_INDUSTRY,
    level: String(profile?.level || '').trim() || DEFAULT_EXPERIENCE_LEVEL,
  };
}

export function resolveCandidateProfileMeta(
  user: Record<string, unknown> | null | undefined,
  prompts: AppPrompt[]
): CandidateProfileMeta {
  const fromPrompt = prompts.find(
    (p) => p.jobTitle?.trim() || p.jobIndustry?.trim() || p.level?.trim()
  );

  return withRolePromptMetaDefaults({
    jobTitle:
      (user?.jobTitle as string) ||
      (user?.role_title as string) ||
      (user?.targetRole as string) ||
      fromPrompt?.jobTitle?.trim() ||
      '',
    jobIndustry:
      (user?.jobIndustry as string) ||
      (user?.industry as string) ||
      fromPrompt?.jobIndustry?.trim() ||
      '',
    level:
      (user?.level as string) ||
      (user?.promptLevel as string) ||
      (user?.prompt_level as string) ||
      (user?.experienceLevel as string) ||
      fromPrompt?.level?.trim() ||
      '',
  });
}

function storedRolePromptsFromUser(
  user: Record<string, unknown> | null | undefined
): RankedPromptChoice[] {
  const raw = user?.roleSpecificPrompts;
  if (!Array.isArray(raw)) return [];
  return choicesFromPromptTexts(raw.map((item) => String(item || '')));
}

export async function fetchRolePromptPoolFromAI(
  profile: CandidateProfileMeta,
  options?: { overview?: string; skills?: string[]; experienceSummary?: string }
): Promise<RankedPromptChoice[]> {
  const meta = withRolePromptMetaDefaults(profile);
  if (!meta.jobTitle?.trim()) {
    return [];
  }

  const accessToken = localStorage.getItem('accessToken');
  if (!accessToken) return [];

  const contextParts = [
    `Role: ${meta.jobTitle}`,
    `Industry: ${meta.jobIndustry}`,
    `Level: ${meta.level}`,
  ];
  if (options?.overview?.trim()) {
    contextParts.push(`Candidate overview: ${options.overview.trim().slice(0, 1200)}`);
  }
  if (options?.skills?.length) {
    contextParts.push(`Key skills: ${options.skills.slice(0, 12).join(', ')}`);
  }
  if (options?.experienceSummary?.trim()) {
    contextParts.push(`Resume experience:\n${options.experienceSummary.trim().slice(0, 2400)}`);
  }

  const question = `${ROLE_PROMPT_AI_INSTRUCTIONS}

${contextParts.join('\n')}`;

  const response = await fetch(`${BASE_URL}${API_ROUTES.AI.ASK_QUESTION}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ question }),
  });

  if (!response.ok) return [];

  const data = await response.json();
  const prompts = parseRankedAiPromptList(data.answer, ROLE_PROMPT_POOL_SIZE);
  if (prompts.length > 0) {
    saveCachedRolePrompts(meta.jobTitle, meta.jobIndustry, meta.level, prompts);
  }
  return prompts;
}

export async function loadRolePromptLibrary(
  profile: CandidateProfileMeta,
  options?: { user?: Record<string, unknown> | null }
): Promise<RankedPromptChoice[]> {
  const meta = withRolePromptMetaDefaults(profile);
  if (!meta.jobTitle) {
    return [];
  }

  const cached = loadCachedRolePrompts(meta.jobTitle, meta.jobIndustry, meta.level);
  if (cached.length > 0) {
    return cached;
  }

  const stored = storedRolePromptsFromUser(options?.user);
  if (stored.length > 0) {
    saveCachedRolePrompts(meta.jobTitle, meta.jobIndustry, meta.level, stored);
    return stored;
  }

  return fetchRolePromptPoolFromAI(meta);
}

export async function refreshRolePromptLibrary(
  profile: CandidateProfileMeta,
  options?: { overview?: string; skills?: string[]; experienceSummary?: string }
): Promise<RankedPromptChoice[]> {
  return fetchRolePromptPoolFromAI(withRolePromptMetaDefaults(profile), options);
}

export function cacheRolePromptsFromTexts(
  profile: CandidateProfileMeta,
  texts: string[]
): RankedPromptChoice[] {
  const meta = withRolePromptMetaDefaults(profile);
  const choices = choicesFromPromptTexts(texts);
  if (meta.jobTitle && choices.length > 0) {
    saveCachedRolePrompts(meta.jobTitle, meta.jobIndustry, meta.level, choices);
  }
  return choices;
}
